import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../utils/navigation';

import ArtistsList from './ArtistsList';
import GalleryGrid from './GalleryGrid';
import MarketTemplate from './MarketTemplate';
import SearchBar from './navbar/components/SearchBar';
import { FilterPanel } from './ui';

const GallerySection = () => {
  const [activeTab, setActiveTab] = useState('Works');
  const [isInitializing, setIsInitializing] = useState(true);
  const { user: currentUser } = useAuth();
  const { navigateToArtist } = useNavigation();
  const { state } = useAppContext();
  const location = useLocation();

  // 恢复Gallery页面的滚动位置和标签页状态
  useEffect(() => {
    // 优先检查导航历史中的fromNavbar状态
    if (state.navigationHistory.length > 0) {
      const lastHistory =
        state.navigationHistory[state.navigationHistory.length - 1];

      console.log('[GallerySection] Navigation history:', lastHistory);

      // 如果是从导航栏直接点击Gallery按钮，总是显示Works标签页
      if (lastHistory.fromNavbar && lastHistory.from === 'gallery') {
        console.log(
          '[GallerySection] From navbar Gallery button, setting Works tab'
        );
        setActiveTab('Works');
        setIsInitializing(false);
        return; // 直接返回，不处理其他状态
      }
    }

    // 处理从UploadSuccess传递过来的状态
    if (location.state?.from === 'upload-success') {
      // 立即设置activeTab，避免状态变化导致的重新渲染
      setActiveTab(location.state.activeTab || 'Works');

      // 如果需要滚动到Gallery标题，使用requestAnimationFrame优化时机
      if (location.state.scrollToGallery) {
        requestAnimationFrame(() => {
          const gallerySection = document.querySelector(
            '[data-section="gallery"]'
          );
          if (gallerySection) {
            const rect = gallerySection.getBoundingClientRect();
            const scrollTop = window.pageYOffset + rect.top - 80; // 减去80px避免遮挡按钮
            window.scrollTo({ top: scrollTop, behavior: 'auto' });
          }
          // 完成初始化
          setIsInitializing(false);
        });
      } else {
        setIsInitializing(false);
      }

      // 清除状态，避免重复处理
      window.history.replaceState({}, document.title);
    }
    // 处理从artist-profile返回的状态
    else if (location.state?.from === 'artist-profile') {
      console.log(
        '[GallerySection] Returning from artist-profile, setting activeTab:',
        location.state.activeTab
      );

      // 设置对应的标签页
      setActiveTab(location.state.activeTab || 'Works');

      // 处理滚动位置恢复
      const savedPositionData = state.scrollPositions['/'];
      if (savedPositionData) {
        const savedPosition =
          typeof savedPositionData === 'object'
            ? savedPositionData.scrollY
            : savedPositionData;
        const elementInfo =
          typeof savedPositionData === 'object'
            ? savedPositionData.elementInfo
            : null;

        console.log(
          '[GallerySection] Restoring saved position:',
          savedPosition
        );
        console.log('[GallerySection] Element info:', elementInfo);

        requestAnimationFrame(() => {
          // 首先恢复滚动位置
          window.scrollTo({ top: savedPosition, behavior: 'auto' });

          // 如果有元素信息，尝试定位到具体元素
          if (elementInfo && elementInfo.type === 'artist') {
            setTimeout(() => {
              const targetElement = document.querySelector(
                `[data-artist-id="${elementInfo.id}"]`
              );
              console.log(
                '[GallerySection] Target element found:',
                !!targetElement
              );
              if (targetElement) {
                // 滚动到元素位置并添加高亮效果
                targetElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                });

                // 添加临时高亮效果（淡蓝色）
                targetElement.classList.add(
                  'bg-blue-50',
                  'border-2',
                  'border-blue-300'
                );
                setTimeout(() => {
                  targetElement.classList.remove(
                    'bg-blue-50',
                    'border-2',
                    'border-blue-300'
                  );
                }, 2000);
              }
            }, 100);
          }
        });
      } else {
        // 如果没有保存的位置信息，滚动到Gallery标题
        if (location.state.scrollToGallery) {
          requestAnimationFrame(() => {
            const gallerySection = document.querySelector(
              '[data-section="gallery"]'
            );
            if (gallerySection) {
              const rect = gallerySection.getBoundingClientRect();
              const scrollTop = window.pageYOffset + rect.top - 80; // 减去80px避免遮挡按钮
              window.scrollTo({ top: scrollTop, behavior: 'auto' });
            }
          });
        }
      }

      // 完成初始化
      setIsInitializing(false);

      // 清除状态，避免重复处理
      window.history.replaceState({}, document.title);
    } else {
      // 原有的滚动位置恢复逻辑
      const savedPositionData = state.scrollPositions['/'];
      const savedPosition =
        typeof savedPositionData === 'object'
          ? savedPositionData?.scrollY
          : savedPositionData;
      if (typeof savedPosition === 'number' && !Number.isNaN(savedPosition)) {
        requestAnimationFrame(() => {
          window.scrollTo({ top: savedPosition, behavior: 'auto' });
        });
      }

      // 恢复标签页状态 - 但优先使用Works作为默认标签页
      if (state.navigationHistory.length > 0) {
        const lastHistory =
          state.navigationHistory[state.navigationHistory.length - 1];
        // 只有当来源是gallery且不是从导航栏直接点击Gallery按钮时，才恢复之前的标签页
        if (
          lastHistory.activeTab &&
          lastHistory.from === 'gallery' &&
          !lastHistory.fromNavbar
        ) {
          setActiveTab(lastHistory.activeTab);
        } else {
          // 默认显示Works标签页
          setActiveTab('Works');
        }
      } else {
        // 没有历史记录时，默认显示Works标签页
        setActiveTab('Works');
      }

      // 完成初始化
      setIsInitializing(false);
    }
  }, [location.state, state.scrollPositions, state.navigationHistory]);

  // 不再需要监听用户切换事件，直接从 useAuth 获取

  const tabs = ['Works', 'Artists', 'Market'];

  // 处理艺术家点击事件
  const handleArtistClick = artwork => {
    navigateToArtist(artwork.id, 'gallery', activeTab);
  };

  // 如果正在初始化，显示加载状态
  if (isInitializing) {
    return (
      <section className='py-16 bg-white' data-section='gallery'>
        <div className='w-full px-4 sm:px-6 lg:px-8'>
          <div className='flex flex-col mb-8'>
            <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-0'>
              {/* Search Bar Placeholder */}
              <div className='w-full md:w-7/12'>
                <div className='h-10 bg-gray-200 rounded-md animate-pulse'></div>
              </div>
              <div className='w-full md:flex-1 md:flex md:justify-end'>
                <div className='inline-flex rounded-md border border-black bg-transparent p-1 w-full md:w-auto'>
                  {tabs.map(tab => (
                    <button
                      key={tab}
                      className={`relative flex-1 md:flex-none px-4 md:px-16 py-3 text-sm md:text-base font-semibold tracking-wide ${
                        activeTab === tab ? 'text-white' : 'text-black'
                      }`}
                    >
                      {activeTab === tab && (
                        <div className='absolute inset-0 bg-black rounded-sm' />
                      )}
                      <span className='relative z-10'>{tab}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className='flex flex-col lg:flex-row gap-4'>
            {/* Filter Panel Placeholder */}
            <div className='hidden lg:block lg:w-64 flex-shrink-0'>
              <div className='bg-gray-200 rounded-lg h-96 animate-pulse'></div>
            </div>
            {/* Content Area Placeholder */}
            <div className='flex-1'>
              <div className='animate-pulse'>
                <div className='h-64 bg-gray-200 rounded-lg'></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className='py-16 bg-white' data-section='gallery'>
      <div className='w-full px-4 sm:px-6 lg:px-8'>
        {/* Section Header */}
        <div className='flex flex-col mb-8'>
          {/* 手机版：搜索框在上，标签在下；桌面版：搜索框在左，标签在右 */}
          <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-0'>
            {/* Search Bar - 手机版全宽，桌面版左对齐 */}
            <div className='w-full md:w-7/12'>
              <SearchBar
                onInputChange={e => console.log('Search:', e.target.value)}
                placeholder='Search for Works or Artists'
                className='w-full'
              />
            </div>

            {/* Segmented Tab Button Group - 手机版全宽，桌面版右对齐 */}
            <div className='w-full md:flex-1 md:flex md:justify-end'>
              <div className='inline-flex rounded-md border border-white/30 bg-white/10 backdrop-blur-md p-1 shadow-lg shadow-black/10 w-full md:w-auto'>
                {tabs.map((tab, index) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`relative flex-1 md:flex-none px-4 md:px-16 py-3 text-sm md:text-base font-semibold tracking-wide transition-all duration-300 ease-in-out group ${
                      activeTab === tab
                        ? 'text-white'
                        : 'text-black group-hover:text-white'
                    }`}
                  >
                    {/* Active Background - 点击后黑色 */}
                    {activeTab === tab && (
                      <div className='absolute inset-0 bg-black rounded-sm transition-all duration-300 ease-in-out' />
                    )}

                    {/* Hover Background - 鼠标悬停蓝色 */}
                    {activeTab !== tab && (
                      <div className='absolute inset-0 bg-transparent group-hover:bg-tag-blue rounded-sm transition-all duration-300 ease-in-out' />
                    )}

                    {/* Text Content */}
                    <span className='relative z-10'>{tab}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className='flex flex-col lg:flex-row gap-4'>
          {/* Filter Panel - Left Sidebar */}
          <div className='hidden lg:block lg:w-64 flex-shrink-0'>
            <FilterPanel />
          </div>

          {/* Content Area - Right Side */}
          <div className='flex-1'>
            {activeTab === 'Works' && <GalleryGrid currentUser={currentUser} />}
            {activeTab === 'Artists' && (
              <ArtistsList onArtistClick={handleArtistClick} />
            )}
            {activeTab === 'Market' && <MarketTemplate />}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GallerySection;
